import React, { useCallback, useMemo } from 'react';

import orderBy from 'lodash.orderby';

import type { RAsyncItemsRendererProps } from '@/_components';
import {
  FormField,
  FormItem,
  FormMessage,
  RAsyncMultiCombobox,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from '@/_components';
import { TableHeading } from '@/_containers';
import type { VendorDetailsFormSchema } from '@/_pages/settings/inventory-kits/shared/schemas/vendor-details';
import { useGetInventoryVendorsMutation } from '@/_pages/settings/inventory-vendors/shared';
import type { InventoryVendorQuerySchema } from '@/_pages/settings/inventory-vendors/shared/schemas';
import { currencyInput } from '@/_schemas/generic';

import { InventoryVendorItem } from './item';
import { NoInventoryVendors } from './no-vendors';

import { useInventoryFormContext } from '../../form-context';

type InventoryVendorsSectionProps = {
  disabled: boolean;
};

export function MultiVendorsSection({ disabled }: InventoryVendorsSectionProps) {
  const form = useInventoryFormContext();
  const [id, vendors, vendorDetails] = form.watch(['id', 'vendors', 'vendor_details']);

  const vendorsMap = useMemo(
    () =>
      vendors?.reduce(
        (acc, v) => {
          acc[v.id] = v;
          return acc;
        },
        {} as Record<string, InventoryVendorQuerySchema>
      ),
    [vendors]
  );

  const getVendorDetailsWithPivot = useCallback(
    (allDetails: VendorDetailsFormSchema[]) => {
      const vendorIds = vendors?.map((v) => v.id) ?? [];
      const vDetails = allDetails.filter((vd) => vendorIds.includes(vd.vendor_id));
      const vendorsWithUpdatedPivot = vDetails.map((v) => ({
        ...v,
        vendor: { ...v.vendor, pivot: vendorsMap?.[v.vendor_id]?.pivot },
      }));
      const sortedAsc = orderBy(vendorsWithUpdatedPivot, ['vendor.name']);
      const primaryVendors = sortedAsc.filter((v) => v.vendor.pivot?.type === 'primary');
      const secondaryVendors = sortedAsc.filter((v) => v.vendor.pivot?.type !== 'primary');
      return [...primaryVendors, ...secondaryVendors];
    },
    [vendors, vendorsMap]
  );

  const setVendorType = useCallback(
    (vendor: InventoryVendorQuerySchema, isCurrentlyPrimary: boolean) => {
      const newVendors = vendors?.map((v) => {
        let nVal = { ...v, pivot: { ...(v.pivot || {}), type: 'secondary' } };

        if (v.id === vendor.id && !isCurrentlyPrimary) {
          nVal = { ...v, pivot: { ...(v.pivot || {}), type: 'primary' } };
        }
        return nVal as InventoryVendorQuerySchema;
      });
      form.setValue('vendors', newVendors, { shouldDirty: true });
    },
    [form, vendors]
  );

  const getSortedVendors = useCallback((values: InventoryVendorQuerySchema[]) => {
    return orderBy(values, ['name']);
  }, []);

  const filterVendors = React.useCallback(
    (item: InventoryVendorQuerySchema) => !!(vendors || []).find(({ id }) => id === item.id),
    [vendors]
  );

  const itemToString = React.useCallback((item: InventoryVendorQuerySchema) => item.name, []);

  const keyExtractor = React.useCallback((item: InventoryVendorQuerySchema) => item.id.toString(), []);

  const handleVendorsChange = React.useCallback(
    (v: InventoryVendorQuerySchema[]) => {
      form.setValue('vendors', v, { shouldDirty: true });

      const newVendorDetails = v
        .map((vendor) => {
          const vendorDetail = vendorDetails?.find((vd) => vd.vendor_id === vendor.id);

          if (!vendorDetail) {
            return {
              id: -1 * vendor.id,
              vendor,
              inventory_id: id,
              vendor_id: vendor.id,
              vendor_sku: '',
              vendor_url: '',
              last_price: currencyInput.defaultValue(),
              vendor_note: '',
            };
          }

          return {
            ...vendorDetail,
            vendor,
          };
        })
        .filter(Boolean);

      form.setValue('vendor_details', newVendorDetails, { shouldDirty: true });
    },
    [form, id, vendorDetails]
  );

  const renderVendorsList: RAsyncItemsRendererProps<InventoryVendorQuerySchema> = React.useCallback(
    ({ removeItem }) => {
      const vendorDetailsWithPivot = getVendorDetailsWithPivot(vendorDetails);

      if (vendorDetailsWithPivot.length === 0) {
        return <NoInventoryVendors />;
      }

      const handleDelete = (vendor: InventoryVendorQuerySchema) => {
        removeItem(vendor);
        // const newVendorDetails = vendorDetailsWithPivot.filter((v) => v.vendor.id !== vendor.id);
        // form.setValue('vendor_details', newVendorDetails, { shouldDirty: true });
      };

      const handleVendorTypeToggle = (vendorDetail: VendorDetailsFormSchema, isPrimary: boolean) => {
        setVendorType(vendorDetail.vendor, isPrimary);
      };

      return (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
              <TableHeading title="Vendor" headerClassName="w-full" />
              <TableHeading title="SKU" headerClassName="w-36 min-w-36" />
              <TableHeading title="URL" headerClassName="w-16 min-w-16" />
              <TableHeading title="Notes" headerClassName="w-16 min-w-16" />
              <TableHeading title="Last Price" headerClassName="min-w-24" />
              <TableHeading title="" headerClassName="w-full" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {vendorDetailsWithPivot.map((item) => (
              <InventoryVendorItem
                key={item.id}
                vendorDetail={item}
                index={vendorDetails.findIndex((v) => v.id === item.id)}
                isEditing={!disabled}
                onDelete={handleDelete}
                setVendorType={handleVendorTypeToggle}
              />
            ))}
          </TableBody>
        </Table>
        // </div>
      );
    },
    [getVendorDetailsWithPivot, vendorDetails, setVendorType, disabled]
  );

  return (
    <>
      <div className="mt-6 flex flex-col gap-2">
        <FormField
          control={form.control}
          name="vendors"
          render={() => (
            <FormItem>
              <RAsyncMultiCombobox<InventoryVendorQuerySchema>
                label="Vendors"
                options={vendors || []}
                value={vendors && vendors.length > 0 ? vendors[0] : ({ id: -1 } as InventoryVendorQuerySchema)}
                onValueChange={handleVendorsChange}
                isEditable={!disabled}
                hideClearIndicator
                filterOptions={filterVendors}
                renderItems={renderVendorsList}
                keyExtractor={keyExtractor}
                itemToString={itemToString}
                sortValues={getSortedVendors}
                useApi={useGetInventoryVendorsMutation}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
